/**
 * Head metadata + JSON-LD for the three revision-surgery spoke pages.
 *
 * Each spoke gets MedicalWebPage, MedicalProcedure, and FAQPage nodes whose
 * `mainEntity` mirrors the visible questions rendered by RevisionSpokePage,
 * which is what scripts/services-faq-sync-check.mjs validates.
 */
import { lastmodFor } from "@/lib/route-lastmod";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { buildFaqPage, faqPageJson } from "@/lib/faq-schema";
import { HOSPITAL_REF, ORG_REF, PHYSICIAN_REF, SETTING_OUTPATIENT, imageRights, workTranslationOf } from "@/lib/medical-schema";
import bypassResectionEn from "@/assets/bypass-revision-resection-en.png.asset.json";
import pouchContrastJpg980 from "@/assets/dilated-pouch-contrast-980.jpg.asset.json";
import pouchAnnotatedJpg980 from "@/assets/dilated-pouch-boxed-en-980.jpg.asset.json";
import { PROCEDURE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";
import sleeveNormalJpg1200 from "@/assets/sleeve-normal-1200.jpg.asset.json";
import sleeveStretchedJpg1200 from "@/assets/sleeve-stretched-1200.jpg.asset.json";
import type { RevisionSpoke } from "@/lib/revision-spokes";
import type { RevisionSpokeMeta, RevisionSlug } from "@/lib/revision-spoke-meta";
import { DEFAULT_OG_IMAGE } from "@/lib/og-image";

const SITE = "https://virginiabariatric.com";

/** Branded 1200x630 cards produced by scripts/generate-revision-og-images.py. */
const SPOKE_CARD_ALT: Record<RevisionSlug, string> = {
  sleeve:
    "Gastric sleeve revision surgery in Northern Virginia with Dr. Matthew Fitzer — laparoscopic view of a normal tubular sleeve outlined in green on a Virginia Bariatric branded card",
  "gastric-bypass":
    "Gastric bypass revision surgery in Northern Virginia with Dr. Matthew Fitzer — upper GI contrast study of a stretched gastric bypass pouch on a Virginia Bariatric branded card",
  "lap-band":
    PROCEDURE_MEDIA.bandDilatedEsophagus.alt.en,
  tore: "Bariatric surgery and weight loss practice in Northern Virginia — Dr. Matthew Fitzer, board-certified bariatric surgeon",
};

/**
 * `meta` carries the handful of fields the browser bundle needs; `spoke` is
 * the full content model and is passed as `null` on the client so the body
 * copy for every spoke page stays out of the shared chunk.
 */
/** In-page clinical photos, in DOM order, for the spokes that show them. */
const SPOKE_PHOTOS: Partial<Record<RevisionSlug, { url: string; caption: string; width: number; height: number }[]>> = {
  "gastric-bypass": [
    {
      url: `${SITE}${pouchContrastJpg980.url}`,
      caption:
        "Stretched pouch in a gastric bypass patient twelve years and two term pregnancies after the initial surgery. The oral contrast has mostly flowed into the small intestine, leaving the pouch filled with CO2 (gas) released by the effervescent crystals swallowed as part of the test.",
      width: 980,
      height: 957,
    },
    {
      url: `${SITE}${pouchAnnotatedJpg980.url}`,
      caption:
        "Same pouch, now with annotation by Dr. Fitzer. The pouch is outlined in blue. A normal pouch is about the same width as the esophagus, so this pouch is clearly larger than it should be. She experienced return of proper weight loss after revision.",
      width: 980,
      height: 957,
    },
    {
      url: `${SITE}${bypassResectionEn.url}`,
      caption:
        "This patient's gastric bypass pouch dilated after two term pregnancies. Dr. Fitzer performed a laparoscopic revision, resecting the excess pouch and creating a new, smaller pouch sized with a bougie.",
      width: 1200,
      height: 866,
    },
  ],
};

const SURGICAL_REVISION =
  "Performed laparoscopically, or endoscopically through the mouth where appropriate, depending on the type of revision.";

/** Care setting per spoke, stated in `howPerformed`. */
const REVISION_HOW_PERFORMED: Record<string, string> = {
  sleeve: `${SURGICAL_REVISION} Surgical sleeve revisions are performed at INOVA Fair Oaks Hospital, usually with a one-night hospital stay.`,
  "gastric-bypass": `${SURGICAL_REVISION} Surgical pouch revision and distalization are performed at INOVA Fair Oaks Hospital, usually with a one-night hospital stay.`,
  "lap-band": `${SURGICAL_REVISION} LAP-BAND removal alone is an outpatient procedure at INOVA Fair Oaks Hospital; patients usually go home the same day. Band-to-sleeve and band-to-bypass conversions are performed at INOVA Fair Oaks Hospital, usually with a one-night hospital stay.`,
  tore: `An endoscope passed through the mouth places stitches to tighten a stretched gastric bypass outlet, with no incisions. ${SETTING_OUTPATIENT}`,
};

export function revisionSpokeHead(meta: RevisionSpokeMeta, spoke: RevisionSpoke | null) {
  const url = `${SITE}${meta.path}`;
  const scan = PROCEDURE_MEDIA.bandDilatedEsophagus;
  const image = meta.slug === "tore"
    ? DEFAULT_OG_IMAGE
    : meta.slug === "lap-band"
      ? `${SITE}${scan.src}`
      : `${SITE}/og/revisions/og-revision-${meta.slug}.jpg`;
  const imageAlt = SPOKE_CARD_ALT[meta.slug];
  const photos = SPOKE_PHOTOS[meta.slug] ?? [];
  const galleryPhotos = meta.slug === "gastric-bypass" || meta.slug === "sleeve"
    ? spoke?.sections.flatMap((section) => section.gallery?.images ?? []) ?? []
    : [];
  const mapPhoto = (photo: { url: string; caption: string; width: number; height: number }) => ({
    "@type": "ImageObject",
    url: photo.url,
    contentUrl: photo.url,
    caption: photo.caption,
    width: photo.width,
    height: photo.height,
    inLanguage: "en-US",
    ...imageRights(url),
  });
  // The normal-pouch scan uses the shared registry builder (like the
  // LAP-BAND page) so its full-size URL, alt text, and AI-assistance
  // credit match the sitemap and the Spanish twin exactly.
  const normalPouchObjects = meta.slug === "gastric-bypass"
    ? procedureImageObjects(["revisionPouchNormalEn"], "en", url)
    : [];
  const photoObjects = meta.slug === "tore"
    ? [mapPhoto({ url: image, caption: SPOKE_CARD_ALT.tore, width: 1200, height: 630 })]
    : meta.slug === "lap-band"
    ? procedureImageObjects(["bandTubing", "bandDilatedEsophagus", "bandRemoved"], "en", url)
    : meta.slug === "sleeve"
      ? [
        ...procedureImageObjects(["sleeveNormalRevision", "sleeveStretchedRevision"], "en", url),
        ...galleryPhotos.map((photo) => mapPhoto({ ...photo, url: `${SITE}${photo.src}` })),
        ...procedureImageObjects(
          ["sleeveUgiNormalEn", "sleeveUgiDilatedEn", "sleeveUgiRefluxPreEn", "sleeveUgiRefluxPostEn"],
          "en",
          url,
        ),
      ]
      : [
        ...photos.slice(0, 2).map(mapPhoto),
        ...normalPouchObjects,
        ...photos.slice(2).map(mapPhoto),
        ...galleryPhotos.map((photo) => mapPhoto({ ...photo, url: `${SITE}${photo.src}` })),
      ];
  const primaryImageObject = meta.slug === "lap-band" ? photoObjects[1] : photoObjects[0];

  return {
    meta: [
      { title: meta.title },
      { name: "description", content: meta.description },
      ...(import.meta.env.SSR ? [
      { property: "og:title", content: meta.title },
      { property: "og:description", content: meta.description },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:image", content: image },
       { property: "og:image:width", content: String(meta.slug === "lap-band" ? scan.width : 1200) },
       { property: "og:image:height", content: String(meta.slug === "lap-band" ? scan.height : 630) },
      { property: "og:image:alt", content: imageAlt },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: meta.title },
      { name: "twitter:description", content: meta.description },
      { name: "twitter:image", content: image },
      { name: "twitter:image:alt", content: imageAlt },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates(meta.path) : []),
      // Preload the LAP-BAND scan's WebP the <picture> element actually uses.
      // imagesrcset/imagesizes must mirror the figure's <source> so the
      // browser preloads the same candidate it will select (800w on most
      // screens) instead of force-fetching the 1268w file a second time.
      ...(meta.slug === "lap-band" ? [{
        rel: "preload",
        as: "image",
        href: scan.webp,
        imagesrcset: scan.webpSrcSet,
        imagesizes: "(min-width: 768px) 300px, calc(100vw - 3rem)",
        type: "image/webp",
      }] : []),
    ],
    scripts: ((import.meta.env.SSR && spoke) ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          "@id": `${url}#webpage`,
          ...(meta.slug === "tore" ? {} : workTranslationOf(spoke.path)),
          ...(spoke.lastReviewed ? { lastReviewed: spoke.lastReviewed } : {}),
          ...(lastmodFor(spoke.path) ? { dateModified: lastmodFor(spoke.path) } : {}),
          reviewedBy: PHYSICIAN_REF,

          name: spoke.webPageName,
          url,
          description: spoke.description,
          mainEntity: { "@id": `${url}#procedure` },
          inLanguage: "en-US",
          isPartOf: {
            "@type": "WebSite",
            name: "Virginia Bariatric Surgery",
            url: SITE,
          },
          significantLink: [`${SITE}/services/revisions`],
          ...(primaryImageObject ? { primaryImageOfPage: primaryImageObject } : {}),
          mentions: [HOSPITAL_REF],
          about: [{ "@id": `${url}#procedure` }, {
            "@type": "MedicalCondition",
            name: "Weight regain and insufficient weight loss after bariatric surgery",
            code: {
              "@type": "MedicalCode",
              codingSystem: "ICD-10",
              codeValue: "E66.9",
            },
          }],
          specialty: { "@type": "MedicalSpecialty", name: "Bariatric Surgery" },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Patient",
            geographicArea: { "@type": "AdministrativeArea", name: "Virginia" },
          },
          mainContentOfPage: { "@type": "WebPageElement", cssSelector: "article" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalProcedure",
          "@id": `${url}#procedure`,
          mainEntityOfPage: { "@id": `${url}#webpage` },
          procedureType: meta.slug === "tore" ? "https://schema.org/NoninvasiveProcedure" : "https://schema.org/SurgicalProcedure",
          name: spoke.procedureName,
          alternateName: spoke.procedureAlternateNames,
          description: spoke.procedureDescription,
          url,
          ...(photoObjects.length
            ? { image: photoObjects.length === 1 ? photoObjects[0] : photoObjects }
            : {}),
          bodyLocation: "Stomach and small intestine",
          howPerformed: REVISION_HOW_PERFORMED[meta.slug],
          preparation: meta.slug === "tore"
            ? "Upper endoscopy and upper GI study to assess the outlet and pouch."
            : "Review of prior operative records, upper endoscopy or contrast imaging to assess current anatomy, nutritional labs, and dietitian and medical evaluations.",
          followup: meta.slug === "tore"
            ? "Staged diet and follow-up in Herndon or by telehealth."
            : "Staged post-operative diet, lifelong vitamin and mineral supplementation, nutritional coaching, activity progression, and long-term medical follow-up.",
          status: "https://schema.org/ActiveActionStatus",
          medicineSystem: "https://schema.org/WesternConventional",
          relevantSpecialty: { "@type": "MedicalSpecialty", name: "Bariatric Surgery" },
          recognizingAuthority: ORG_REF,
        }),
      },
      {
        type: "application/ld+json",
        children: faqPageJson(
          buildFaqPage(spoke.faqs, {
            id: `${url}#faq`,
            url,
            name: spoke.faqHeading ?? `${spoke.h1}: frequently asked questions`,
          }) ?? {},
        ),
      },
    ] : []).filter((script) => script.children !== ""),
  };
}
